import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { logger } from '../lib/logger';
import { notificationService } from '../lib/audio/notificationService';
import { analyticsService } from '../lib/analytics/analyticsService';

interface LivechatFrameProps {
  url: string;
  onClose: () => void;
  onMessage?: () => void;
  config: any;
  visible: boolean;
}

export const LivechatFrame: React.FC<LivechatFrameProps> = ({
  url,
  onClose,
  config,
  visible,
  onMessage
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Initialiser l'iframe seulement la première fois
    if (!initializedRef.current && iframeRef.current) {
      logger.debug('Initializing iframe with URL', {
        category: 'Livechat',
        data: { url }
      });
      iframeRef.current.src = url;
      initializedRef.current = true;

      // Track chat open
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'livechat_open',
        event_data: {
          url
        }
      });
    }
  }, [url]);

  // Écouter les messages de l'iframe uniquement quand le chat n'est pas visible
  useEffect(() => {
    // Ne pas mettre en place l'écouteur si le chat est visible
    if (visible) {
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      // Vérifier que le message vient bien du livechat
      if (event.origin === 'https://app.ideta.io') {
        logger.debug('Message received from livechat while hidden', {
          category: 'Livechat',
          data: { 
            origin: event.origin,
            type: event.data?.type,
            timestamp: new Date().toISOString(),
            appState: document.visibilityState
          }
        });

        // Track message received
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'livechat_message_received',
          event_data: {
            visible: false,
            app_state: document.visibilityState
          }
        });

        // Notifier le parent qu'un message a été reçu
        // Uniquement si l'application est en arrière-plan ou le chat est caché
        if (document.visibilityState === 'hidden' || !visible) {
          onMessage?.();
          
          // Jouer le son de notification
          await notificationService.play();

          // Si nous sommes en mode PWA, envoyer une notification
          if (window.matchMedia('(display-mode: standalone)').matches && 'Notification' in window) {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                new Notification('Nouveau message', {
                  icon: '/icon/icon_192.png',
                  badge: '/icon/icon_192.png',
                  tag: 'livechat-message'
                });
              }
            });
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Nettoyer l'écouteur quand le composant est démonté ou quand visible change
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [visible, onMessage]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      logger.debug('Closing via overlay click', {
        category: 'Livechat'
      });

      // Track chat close
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'livechat_close',
        event_data: {
          close_method: 'overlay'
        }
      });

      onClose();
    }
  };

  const handleCloseClick = () => {
    logger.debug('Closing via button click', {
      category: 'Livechat'
    });

    // Track chat close
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'livechat_close',
      event_data: {
        close_method: 'button'
      }
    });

    onClose();
  };

  return (
    <div 
      className="fixed inset-0 transition-all duration-200 livechat-modal"
      style={{
        backgroundColor: visible ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
        zIndex: 9999,
        pointerEvents: visible ? 'auto' : 'none',
        opacity: visible ? 1 : 0,
        visibility: visible ? 'visible' : 'hidden'
      }}
      onClick={handleOverlayClick}
    >
      <div 
        className="absolute inset-4 flex flex-col overflow-hidden transition-transform duration-200 mx-auto"
        style={{ 
          borderRadius: config.border_radius,
          transform: visible ? 'scale(1)' : 'scale(0.95)',
          opacity: visible ? 1 : 0,
          backgroundColor: '#FFFFFF',
          maxWidth: '700px'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex justify-end p-2 border-b"
          style={{ backgroundColor: config['dark-color'] }}
        >
          <button
            onClick={handleCloseClick}
            className="p-2 rounded-full text-white hover:bg-white/10 transition-colors"
            aria-label="Fermer le chat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Container pour l'iframe */}
        <div className="flex-1 w-full bg-white">
          <iframe
            ref={iframeRef}
            className="w-full h-full border-0"
            title="Livechat"
            allow="microphone; camera"
          />
        </div>
      </div>
    </div>
  );
};